import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileSignature, Plus, Settings2, Trash2 } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import type { ContractPlaceholderDefDto, ContractPlaceholderType, ContractTemplateDto } from "@finora/types";
import { api } from "../../lib/api";

/**
 * Pun katalog placeholder-a stvarnog šablona ugovora (38 komada, vidi
 * contract-placeholder.util.ts na backendu, CONTRACT_PLACEHOLDER_DEFS -
 * ovde je ručno prepisano da admin ekran ne zavisi od dodatnog API poziva).
 * `global: true` = vrednost se NE računa po firmi nego dolazi iz
 * ContractProviderSettings (panel ispod, isti za SVAKI ugovor); ostalo se
 * automatski mapira iz Company/Subscription/Package/Contract konkretne
 * firme - admin ne unosi ništa po firmi za te placeholdere.
 */
const BUILT_IN_PLACEHOLDERS: { key: string; label: string; global?: boolean }[] = [
  { key: "company_name", label: "Korisnik - naziv firme" },
  { key: "company_tax_id", label: "Korisnik - PIB" },
  { key: "company_registration_number", label: "Korisnik - matični broj" },
  { key: "company_address", label: "Korisnik - ulica i broj" },
  { key: "company_city", label: "Korisnik - mesto (sa PTT brojem)" },
  { key: "representative_name", label: "Korisnik - zastupnik (OWNER nalog firme)" },
  { key: "signing_place", label: "Mesto zaključenja (= grad Korisnika)" },
  { key: "contract_date", label: "Datum zaključenja" },
  { key: "provider_name", label: "Isporučilac - naziv (matična firma)" },
  { key: "provider_city", label: "Isporučilac - mesto" },
  { key: "provider_address", label: "Isporučilac - ulica i broj" },
  { key: "provider_registration_number", label: "Isporučilac - matični broj" },
  { key: "provider_tax_id", label: "Isporučilac - PIB" },
  { key: "provider_representative", label: "Isporučilac - zastupnik", global: true },
  { key: "provider_bank_account", label: "Isporučilac - tekući račun" },
  { key: "esir_approval_number", label: "Broj odobrenja ESIR-a", global: true },
  { key: "esir_approval_date", label: "Datum odobrenja ESIR-a", global: true },
  { key: "package_name", label: "Naziv paketa" },
  { key: "user_count", label: "Broj korisnika/licenci" },
  { key: "location_count", label: "Broj prodajnih mesta" },
  { key: "device_count", label: "Broj uređaja" },
  { key: "modules", label: "Dodatne funkcionalnosti (moduli)" },
  { key: "monthly_price", label: "Mesečna cena bez PDV-a" },
  { key: "vat_amount", label: "PDV (stopa i iznos)" },
  { key: "monthly_total", label: "Ukupno sa PDV-om" },
  { key: "sla_label", label: "SLA nivo (naziv)" },
  { key: "payment_due_days", label: "Rok plaćanja u danima", global: true },
  { key: "trajanje_neodredjeno", label: "Čekboks - neodređeno vreme" },
  { key: "trajanje_12", label: "Čekboks - 12 meseci" },
  { key: "trajanje_24", label: "Čekboks - 24 meseca" },
  { key: "duration_label", label: "Odabrani model trajanja (tekst)" },
  { key: "contract_start_date", label: "Datum aktiviranja usluge" },
  { key: "contract_end_date", label: "Datum isteka ugovora" },
  { key: "sla_standard", label: "Čekboks - Standard podrška" },
  { key: "sla_premium", label: "Čekboks - Premium podrška" },
  { key: "sla_premium_price", label: "Cena Premium podrške", global: true },
  { key: "suspension_days", label: "Rok pre suspenzije zbog duga", global: true },
  { key: "court_city", label: "Nadležni sud - grad", global: true },
];

function emptyPlaceholder(): ContractPlaceholderDefDto {
  return { key: "", type: "text", label: "" };
}

/** YYYY-MM-DD <-> ISO datetime (00:00 UTC) helper za esirApprovalDate input-e. */
function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}
function dateInputToIso(value: string): string | null {
  if (!value) return null;
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

/**
 * Panel za ContractProviderSettings (globalno, isto za SVAKI ugovor -
 * zastupnik Isporučioca, ESIR odobrenje, rokovi, cena Premium podrške).
 * Dodato 01.10.2026 - zamena za ukinutu po-firmi "Podaci za ugovor
 * (ContractSettings)" sekciju na profilu firme.
 */
function ProviderSettingsPanel() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["contract-provider-settings"],
    queryFn: () => api.getContractProviderSettings(),
  });

  const [form, setForm] = useState<{
    representativeName: string;
    esirApprovalNumber: string;
    esirApprovalDate: string;
    paymentDueDays: string;
    suspensionDays: string;
    courtCity: string;
    slaPremiumPrice: string;
    contractDiscount12Percent: string;
    contractDiscount24Percent: string;
  } | null>(null);

  useEffect(() => {
    if (data && !form) {
      setForm({
        representativeName: data.representativeName ?? "",
        esirApprovalNumber: data.esirApprovalNumber ?? "",
        esirApprovalDate: isoToDateInput(data.esirApprovalDate),
        paymentDueDays: String(data.paymentDueDays),
        suspensionDays: String(data.suspensionDays),
        courtCity: data.courtCity,
        slaPremiumPrice: data.slaPremiumPrice,
        contractDiscount12Percent: data.contractDiscount12Percent ?? "10",
        contractDiscount24Percent: data.contractDiscount24Percent ?? "15",
      });
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("no form");
      return api.upsertContractProviderSettings({
        representativeName: form.representativeName || null,
        esirApprovalNumber: form.esirApprovalNumber || null,
        esirApprovalDate: dateInputToIso(form.esirApprovalDate),
        paymentDueDays: Number(form.paymentDueDays) || 8,
        suspensionDays: Number(form.suspensionDays) || 15,
        courtCity: form.courtCity || "Nišu",
        slaPremiumPrice: Number(form.slaPremiumPrice) || 0,
        contractDiscount12Percent: Number(form.contractDiscount12Percent) || 0,
        contractDiscount24Percent: Number(form.contractDiscount24Percent) || 0,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contract-provider-settings"] }),
  });

  if (isLoading || !form) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="mb-1 flex items-center gap-2">
        <Settings2 className="h-4 w-4 text-slate-400" aria-hidden />
        <h2 className="text-sm font-semibold text-slate-800">Globalni podaci za ugovor</h2>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Ovi podaci su isti za SVAKI ugovor (ne po firmi) - popunjavaju placeholdere obeležene{" "}
        <span className="rounded bg-amber-100 px-1 text-amber-700">globalno</span> u listi ispod. Ostali placeholderi
        (naziv firme, PIB, paket, cena...) se automatski mapiraju iz podataka konkretne firme.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-slate-500">Isporučilac - zastupnik</label>
          <input
            value={form.representativeName}
            onChange={(e) => setForm({ ...form, representativeName: e.target.value })}
            placeholder="Ime i prezime zastupnika"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Isporučilac - tekući račun</label>
          <p className="mt-1.5 text-xs text-slate-400">Automatski iz profila matične firme.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Broj odobrenja ESIR-a</label>
          <input
            value={form.esirApprovalNumber}
            onChange={(e) => setForm({ ...form, esirApprovalNumber: e.target.value })}
            placeholder="npr. 011-00-123/2026"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Datum odobrenja ESIR-a</label>
          <input
            type="date"
            value={form.esirApprovalDate}
            onChange={(e) => setForm({ ...form, esirApprovalDate: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Rok plaćanja (dana)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={form.paymentDueDays}
            onChange={(e) => setForm({ ...form, paymentDueDays: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Rok pre suspenzije zbog duga (dana)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={form.suspensionDays}
            onChange={(e) => setForm({ ...form, suspensionDays: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Nadležni sud - grad</label>
          <input
            value={form.courtCity}
            onChange={(e) => setForm({ ...form, courtCity: e.target.value })}
            placeholder="npr. Nišu"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Cena Premium podrške (RSD/mes.)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.slaPremiumPrice}
            onChange={(e) => setForm({ ...form, slaPremiumPrice: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Popust za ugovor na 12 meseci (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={form.contractDiscount12Percent}
            onChange={(e) => setForm({ ...form, contractDiscount12Percent: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Popust za ugovor na 24 meseca (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={form.contractDiscount24Percent}
            onChange={(e) => setForm({ ...form, contractDiscount24Percent: e.target.value })}
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>
      {save.error && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
        </p>
      )}
      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Čuvam..." : "Sačuvaj"}
        </Button>
      </div>
    </div>
  );
}

/**
 * HAfiscal Admin -> Šabloni ugovora ("Ugovor" modul).
 * PREPRAVLJENO 01.10.2026: realan šablon ima 38 placeholder-a, od kojih se
 * skoro svi automatski mapiraju iz podataka firme/paketa/ugovora - admin ih
 * ovde samo vidi kao referencu i ubacuje u tekst šablona. Preostali
 * (obeleženi "globalno") se podešavaju jednom, ispod, u
 * ProviderSettingsPanel-u (zamena za ukinutu "Podaci za ugovor
 * (ContractSettings)" sekciju po firmi).
 */
export function ContractTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: () => api.listContractTemplates(),
  });

  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
  const [name, setName] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [placeholders, setPlaceholders] = useState<ContractPlaceholderDefDto[]>([]);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);

  const current = selectedId && selectedId !== "new" ? templates?.find((t) => t.id === selectedId) : null;

  useEffect(() => {
    if (selectedId === "new" && hydratedFor !== "new") {
      setName("");
      setBodyText("");
      setPlaceholders([]);
      setHydratedFor("new");
    } else if (current && hydratedFor !== current.id) {
      setName(current.name);
      setBodyText(current.bodyText);
      setPlaceholders(current.placeholders ?? []);
      setHydratedFor(current.id);
    }
  }, [selectedId, current, hydratedFor]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["contract-templates"] });

  const create = useMutation({
    mutationFn: () => api.createContractTemplate({ name, bodyText, placeholders: placeholders.filter((p) => p.key) }),
    onSuccess: (created: ContractTemplateDto) => {
      invalidate();
      setSelectedId(created.id);
      setHydratedFor(created.id);
    },
  });

  const update = useMutation({
    mutationFn: () =>
      api.updateContractTemplate(current!.id, { name, bodyText, placeholders: placeholders.filter((p) => p.key) }),
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: (tpl: ContractTemplateDto) => api.updateContractTemplate(tpl.id, { isActive: !tpl.isActive }),
    onSuccess: invalidate,
  });

  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;

  function insertPlaceholder(key: string) {
    const textarea = textareaRef.current;
    if (!textarea) {
      setBodyText((prev) => `${prev}{${key}}`);
      return;
    }
    const start = textarea.selectionStart ?? bodyText.length;
    const end = textarea.selectionEnd ?? bodyText.length;
    const next = `${bodyText.slice(0, start)}{${key}}${bodyText.slice(end)}`;
    setBodyText(next);
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + key.length + 2;
      textarea.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>

      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light text-brand">
          <FileSignature className="h-6 w-6" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Šabloni ugovora</h1>
          <p className="text-sm text-slate-500">
            Napišite tekst ugovora i definišite placeholdere u vitičastim zagradama (npr.{" "}
            <code className="rounded bg-slate-100 px-1">{"{company_name}"}</code>) - sistem ih pri generisanju
            automatski popunjava iz podataka firme, paketa i pretplate (skoro sve) ili iz globalnih podešavanja ispod
            (zastupnik Isporučioca, ESIR odobrenje, rokovi, cena Premium podrške).
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid gap-4 md:grid-cols-[240px_1fr]">
          <div className="space-y-2">
            <Button size="sm" className="w-full" onClick={() => setSelectedId("new")}>
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Novi šablon
            </Button>
            {(templates ?? []).map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${
                  selectedId === t.id ? "border-brand bg-brand-light text-brand" : "border-border hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{t.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      t.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {t.isActive ? "Aktivan" : "Neaktivan"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-400">{t.placeholders?.length ?? 0} placeholder-a</p>
              </button>
            ))}
            {(templates ?? []).length === 0 && (
              <p className="px-1 text-xs text-slate-400">Još nema nijednog šablona.</p>
            )}
          </div>

          {selectedId === null ? (
            <div className="flex items-center justify-center rounded-xl border border-dashed border-border p-10 text-sm text-slate-400">
              Izaberi šablon sa liste ili napravi novi.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
                <label className="mb-1 block text-xs text-slate-500">Naziv šablona</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="npr. Standardni ugovor HAFiskal ERP"
                  className="mb-3 h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />

                <label className="mb-1 block text-xs text-slate-500">Tekst ugovora</label>
                <textarea
                  ref={textareaRef}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  spellCheck={false}
                  className="h-[360px] w-full rounded-lg border border-border p-2 font-mono text-xs focus:border-brand focus:outline-none"
                />

                <div className="mt-3">
                  <p className="mb-1.5 text-xs font-medium text-slate-500">
                    Ugrađeni placeholderi (klik = ubaci na kursor). Žuto obeleženi su{" "}
                    <span className="rounded bg-amber-100 px-1 text-amber-700">globalno</span> - podešavaju se u
                    panelu ispod, ne po firmi.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {BUILT_IN_PLACEHOLDERS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        title={p.label}
                        onClick={() => insertPlaceholder(p.key)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-medium hover:bg-slate-100 ${
                          p.global ? "border-amber-200 bg-amber-50 text-amber-700" : "border-border bg-app-bg text-slate-600"
                        }`}
                      >
                        {`{${p.key}}`}
                      </button>
                    ))}
                  </div>
                </div>

                {saveError && (
                  <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {saveError instanceof ApiError ? saveError.response.message : "Čuvanje nije uspelo."}
                  </p>
                )}

                <div className="mt-3 flex items-center justify-between">
                  {current ? (
                    <button
                      onClick={() => toggleActive.mutate(current)}
                      className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-app-bg"
                    >
                      {current.isActive ? "Deaktiviraj" : "Aktiviraj"}
                    </button>
                  ) : (
                    <span />
                  )}
                  <Button
                    size="sm"
                    disabled={!name || !bodyText || saving}
                    onClick={() => (selectedId === "new" ? create.mutate() : update.mutate())}
                  >
                    {saving ? "Čuvam..." : "Sačuvaj"}
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-slate-800">Custom placeholderi</h2>
                  <Button size="sm" variant="secondary" onClick={() => setPlaceholders((prev) => [...prev, emptyPlaceholder()])}>
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    Dodaj
                  </Button>
                </div>
                <p className="mb-3 text-xs text-slate-400">
                  Za polja koja ne postoje ugrađeno - ne mapiraju se automatski, pa trenutno nemaju izvor vrednosti
                  (koriste se samo ako naknadno dodate poseban izvor podataka za njih).
                </p>
                {placeholders.length === 0 ? (
                  <p className="text-xs text-slate-400">Nema definisanih custom placeholder-a.</p>
                ) : (
                  <div className="space-y-2">
                    {placeholders.map((p, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_1fr_auto] items-center gap-2">
                        <input
                          value={p.key}
                          onChange={(e) =>
                            setPlaceholders((prev) => prev.map((pp, i) => (i === idx ? { ...pp, key: e.target.value } : pp)))
                          }
                          placeholder="ključ (npr. napomena)"
                          className="h-8 rounded-lg border border-border px-2 text-xs font-mono focus:border-brand focus:outline-none"
                        />
                        <select
                          value={p.type}
                          onChange={(e) =>
                            setPlaceholders((prev) =>
                              prev.map((pp, i) => (i === idx ? { ...pp, type: e.target.value as ContractPlaceholderType } : pp)),
                            )
                          }
                          className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                        >
                          <option value="text">tekst</option>
                          <option value="boolean">da/ne</option>
                        </select>
                        <input
                          value={p.label}
                          onChange={(e) =>
                            setPlaceholders((prev) => prev.map((pp, i) => (i === idx ? { ...pp, label: e.target.value } : pp)))
                          }
                          placeholder="opis (za admina)"
                          className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                        />
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Ubaci na kursor"
                            disabled={!p.key}
                            onClick={() => insertPlaceholder(p.key)}
                            className="rounded-md border border-border px-2 py-1 text-[11px] text-slate-500 hover:bg-app-bg disabled:opacity-40"
                          >
                            Ubaci
                          </button>
                          <button
                            type="button"
                            onClick={() => setPlaceholders((prev) => prev.filter((_, i) => i !== idx))}
                            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <ProviderSettingsPanel />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
